import { useState, useEffect } from "react";
import styled from "styled-components";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Link } from "react-router-dom";
import { Movie } from "../../types";

interface FeaturedCarouselProps {
  movies: Movie[];
}

const CarouselContainer = styled.div`
  position: relative;
  width: 100%;
  height: 50vh;
  min-height: 350px;
  max-height: 500px;
  margin-bottom: var(--spacing-6);
  overflow: hidden;
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);

  @media (max-width: 768px) {
    height: 45vh;
    min-height: 300px;
    max-height: 400px;
    margin-bottom: var(--spacing-4);
    border-radius: 8px;
  }

  @media (max-width: 480px) {
    height: 40vh;
    min-height: 250px;
    max-height: 350px;
    margin-bottom: var(--spacing-3);
    border-radius: 6px;
  }
`;

const Slide = styled.div<{ $active: boolean }>`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  opacity: ${(props) => (props.$active ? 1 : 0)};
  transition: opacity 0.8s ease-in-out;
  pointer-events: ${(props) => (props.$active ? "auto" : "none")};
`;

const BackdropImage = styled.div<{ $backdrop: string }>`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: ${(
    props,
  ) => `linear-gradient(rgba(0, 0, 0, 0.3), rgba(0, 0, 0, 0.6)),
    url(https://image.tmdb.org/t/p/original${props.$backdrop})`};
  background-size: cover;
  background-position: center;
  filter: contrast(1.05) brightness(1.1);
  transform: scale(1.05);
  transition: all 0.5s ease;

  &::before,
  &::after {
    content: "";
    position: absolute;
    top: 0;
    bottom: 0;
    width: 25%;
    pointer-events: none;
    z-index: 2;
    transition: opacity 0.3s ease;
  }

  &::before {
    left: 0;
    background: linear-gradient(
      90deg,
      rgba(0, 0, 0, 0.6) 0%,
      rgba(0, 0, 0, 0.4) 20%,
      rgba(0, 0, 0, 0.2) 40%,
      rgba(0, 0, 0, 0.1) 60%,
      transparent 100%
    );
  }

  &::after {
    right: 0;
    background: linear-gradient(
      -90deg,
      rgba(0, 0, 0, 0.6) 0%,
      rgba(0, 0, 0, 0.4) 20%,
      rgba(0, 0, 0, 0.2) 40%,
      rgba(0, 0, 0, 0.1) 60%,
      transparent 100%
    );
  }

  ${Slide}:hover & {
    transform: scale(1.02);

    &::before,
    &::after {
      opacity: 0.9;
      width: 30%;
    }
  }
`;

const Content = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  padding: var(--spacing-6);
  background: linear-gradient(to top, rgba(0, 0, 0, 0.9), rgba(0, 0, 0, 0));
  color: white;
  z-index: 2;

  @media (max-width: 768px) {
    padding: var(--spacing-4);
  }

  @media (max-width: 480px) {
    padding: var(--spacing-3);
  }
`;

const Title = styled.h2`
  font-size: 2.5rem;
  margin-bottom: var(--spacing-3);
  font-weight: bold;

  @media (max-width: 768px) {
    font-size: 2rem;
    margin-bottom: var(--spacing-2);
  }

  @media (max-width: 480px) {
    font-size: 1.5rem;
    margin-bottom: var(--spacing-2);
  }
`;

const Overview = styled.p`
  font-size: 1.1rem;
  max-width: 800px;
  margin-bottom: var(--spacing-4);
  line-height: 1.6;

  @media (max-width: 768px) {
    font-size: 1rem;
    margin-bottom: var(--spacing-3);
    display: -webkit-box;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }

  @media (max-width: 480px) {
    font-size: 0.9rem;
    margin-bottom: var(--spacing-2);
    -webkit-line-clamp: 2;
  }
`;

const CarouselButton = styled.button<{ $direction: "left" | "right" }>`
  position: absolute;
  top: 50%;
  ${(props) => props.$direction}: var(--spacing-4);
  transform: translateY(-50%);
  background: rgba(0, 0, 0, 0.6);
  border: none;
  border-radius: 50%;
  width: 44px;
  height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
  color: var(--color-text-primary);
  z-index: 3;
  backdrop-filter: blur(4px);

  &:hover {
    background: rgba(0, 0, 0, 0.8);
    transform: translateY(-50%) scale(1.1);
    box-shadow: 0 0 20px rgba(0, 0, 0, 0.3);
  }

  @media (max-width: 768px) {
    width: 36px;
    height: 36px;
    ${(props) => props.$direction}: var(--spacing-2);
  }

  @media (max-width: 480px) {
    width: 32px;
    height: 32px;
    ${(props) => props.$direction}: var(--spacing-1);
    background: rgba(0, 0, 0, 0.8);

    &:hover {
      transform: translateY(-50%);
      box-shadow: none;
    }
  }
`;

const Indicators = styled.div`
  position: absolute;
  bottom: var(--spacing-4);
  right: var(--spacing-4);
  display: flex;
  gap: var(--spacing-2);
  z-index: 3;
  padding: var(--spacing-2);
  background: rgba(0, 0, 0, 0.3);
  border-radius: 20px;
  backdrop-filter: blur(4px);

  @media (max-width: 768px) {
    bottom: var(--spacing-3);
    right: var(--spacing-3);
    padding: var(--spacing-1);
    gap: var(--spacing-1);
  }

  @media (max-width: 480px) {
    display: none;
  }
`;

const Indicator = styled.button<{ $active: boolean }>`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: ${(props) =>
    props.$active ? "var(--color-primary)" : "rgba(255, 255, 255, 0.5)"};
  border: none;
  padding: 0;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    transform: scale(1.2);
  }

  @media (max-width: 768px) {
    width: 4px;
    height: 4px;

    &:hover {
      transform: none;
    }
  }
`;

export const FeaturedCarousel = ({ movies = [] }: FeaturedCarouselProps) => {
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    if (!movies?.length) return;

    const timer = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % movies.length);
    }, 5000);

    return () => clearInterval(timer);
  }, [movies?.length]);

  const handlePrevious = () => {
    if (!movies?.length) return;
    setCurrentIndex((prev) => (prev - 1 + movies.length) % movies.length);
  };

  const handleNext = () => {
    if (!movies?.length) return;
    setCurrentIndex((prev) => (prev + 1) % movies.length);
  };

  if (!movies?.length) return null;

  return (
    <CarouselContainer>
      {movies.map((movie, index) => (
        <Slide key={movie.id} $active={index === currentIndex}>
          <Link to={`/movie/${movie.id}`}>
            <BackdropImage $backdrop={movie.backdrop_path} />
            <Content>
              <Title>{movie.title}</Title>
            </Content>
          </Link>
        </Slide>
      ))}

      <CarouselButton $direction="left" onClick={handlePrevious}>
        <ChevronLeft size={20} />
      </CarouselButton>
      <CarouselButton $direction="right" onClick={handleNext}>
        <ChevronRight size={20} />
      </CarouselButton>

      <Indicators>
        {movies.map((_, index) => (
          <Indicator
            key={index}
            $active={index === currentIndex}
            onClick={() => setCurrentIndex(index)}
          />
        ))}
      </Indicators>
    </CarouselContainer>
  );
};
